import Box from '@mui/material/Box';
import { memo, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { DebugLevel } from './_configs/types';
import { useAppDispatch } from './_flux/Store';
import { standardViewOptions } from './_flux/defaults';
import getCar from './_tools/axios/cars/getCar';
import handleCarNavigateData from './_tools/axios/cars/helpers/handleCarNavigateData';
import { useModuleSkeletonContext } from './context/module-skeleton';
import { useSettingsContext } from './context/settings';
import { Modules } from './modules/_definition/types';
import { fahrzeugsucheModuleName } from './modules/fahrzeugsuche/types';
import { generateLink } from './utils/generateLink';
import stringToSlug from './utils/string-to-slug';
import checkUndefinedNullOrEmpty from './utils/tools/checkUndefinedNullOrEmpty';
import enhancedNavigate from './utils/tools/enhancedNavigate';
import getFluxFunctionsFromStore from './utils/tools/flux-tools';
import { type AppProps } from './vars';

function OldApp(props: AppProps) {
    const { Component, module } = props;
    const settings = useSettingsContext();
    const Skeleton = useModuleSkeletonContext();
    const loc = useLocation();
    const [loading, setLoading] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const { setViewOptions } = module === Modules.Fahrzeugsuche ? getFluxFunctionsFromStore(module, dispatch) : {};

    const checkUrlForRedirect = useCallback(async () => {
        const checkMagicLink = loc?.pathname?.match(/\/magicId\/([0-9]+)/);
        if (!checkUndefinedNullOrEmpty(checkMagicLink?.[1])) {
            try {
                const rawCar = (await getCar(parseFloat(checkMagicLink?.[1]), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
                const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
                if (!checkUndefinedNullOrEmpty(rawCar)) {
                    if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                        console.log(`${fahrzeugsucheModuleName} - OldApp - Redirect magicId - Redirecting to new url`, carLink);
                    }
                    enhancedNavigate(carLink, undefined, true);
                }
            } catch {
                //
            }
        } else {
            const checkSourceLink = loc?.search?.match(/(\?|&)source=([0-9]+)/);
            if (!checkUndefinedNullOrEmpty(checkSourceLink?.[2])) {
                try {
                    const rawCar = (await getCar(parseFloat(checkSourceLink?.[2]), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
                    const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
                    if (!checkUndefinedNullOrEmpty(rawCar)) {
                        if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                            console.log(`${fahrzeugsucheModuleName} - OldApp - Redirect source - Redirecting to new url`, carLink);
                        }
                        enhancedNavigate(carLink, undefined, true);
                    }
                } catch {
                    //
                }
            } else {
                const checkEinzelansicht = loc?.search?.replace('?', '')?.match(/.*_carId[0-9]+/g);
                if (!checkUndefinedNullOrEmpty(checkEinzelansicht)) {
                    try {
                        const [carName, carId] = checkEinzelansicht[0].split('_carId');

                        if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                            console.log(`${module} - OldApp - Redirect _carId - Setting viewOptions to car`, [stringToSlug(carName || ''), carId || 0]);
                        }
                        setViewOptions?.({
                            ...standardViewOptions,
                            car: [carName, parseFloat(carId)],
                            view: settings?.fahrzeugsuche?.listView,
                        });
                    } catch {
                        //
                    }
                } else {
                    setViewOptions?.({
                        ...standardViewOptions,
                        view: settings?.fahrzeugsuche?.listView,
                    });
                }
            }
        }

        setLoading(false);
    }, [loc?.pathname, loc?.search, module, setViewOptions, settings]);

    useEffect(() => {
        checkUrlForRedirect();
    }, [checkUrlForRedirect]);

    return loading ? (
        loc?.search?.includes('_carId') ? Skeleton.details : Skeleton.module
    ) : (
        <Box data-testid="app" sx={(theme) => ({ fontSize: theme?.rawTypography?.body1?.fontSize })}>
            <span id={module + 'ScrollTop'} style={{ scrollMarginTop: settings?.style?.scrollOffset }} />
            <Component />
        </Box>
    );
}

export default memo(OldApp);
